import { useState } from "react";
import { Stethoscope, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

// Platform Super Admin: re-run OCR for ONE page from the durable original and show the instrumented trace. No document/usage mutation.
export function OcrDiagnostic({ docId, pageCount, failed }) {
  const [page, setPage] = useState(failed?.[0] || 1);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async () => {
    setBusy(true); setErr(""); setRes(null);
    try { const { data } = await api.post(`/platform/documents/${docId}/ocr-diagnostic`, null, { params: { page }, timeout: 0 }); setRes(data); }
    catch (e) { setErr(apiError(e, "Diagnostic failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border border-slate-800 rounded-md p-2" data-testid="ocr-diagnostic">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-400">Reproduce OCR for page</span>
        <input type="number" min={1} max={pageCount} value={page} onChange={(e) => setPage(Number(e.target.value))} className="sc-input w-20 text-xs" data-testid="ocr-diagnostic-page" />
        <button onClick={run} disabled={busy} className="inline-flex items-center gap-1 border border-slate-700 hover:border-amber-500 rounded px-2 py-1 disabled:opacity-50" data-testid="ocr-diagnostic-run">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />} Run diagnostic
        </button>
        {failed?.length > 0 && <span className="text-slate-500">failed pages: {failed.slice(0, 20).join(", ")}{failed.length > 20 ? "…" : ""}</span>}
      </div>
      {err && <div className="text-red-400 text-xs mt-1" data-testid="ocr-diagnostic-error">{err}</div>}
      {res && (
        <div className="mt-2 text-[11px] font-mono" data-testid="ocr-diagnostic-result">
          <div className={res.disposition?.startsWith("failed") ? "text-red-300" : "text-emerald-300"} data-testid="ocr-diagnostic-disposition">
            Disposition: {res.disposition}{res.recovered_chars != null ? ` · ${res.recovered_chars} chars · conf ${res.confidence} · ${res.boxes} boxes` : ""}{res.escalation_reason ? ` · escalation reason: ${res.escalation_reason}` : " · no escalation reason"}{res.gemini_invoked ? " · Gemini invoked" : " · Gemini not invoked"}
          </div>
          {res.exception && <div className="text-red-300">Exception: {res.exception} — {res.message}</div>}
          {res.steps.map((s, i) => <div key={i} className={s.ok ? "text-slate-300" : "text-red-300"}>{s.ok ? "✓" : "✗"} {s.step} · {s.ms} ms{s.bytes ? ` · ${s.bytes} bytes` : ""}{s.png_bytes ? ` · ${s.png_bytes} png bytes` : ""}{s.ink_ratio != null ? ` · ink ${s.ink_ratio}` : ""}{s.chars != null ? ` · ${s.chars} chars · ${s.boxes} boxes · conf ${s.confidence}` : ""}{s.exception ? ` · ${s.exception}: ${s.message}` : ""}</div>)}
          {res.recovered_excerpt && <div className="text-slate-400 mt-1 whitespace-pre-wrap" data-testid="ocr-diagnostic-excerpt">Recovered text (excerpt): {res.recovered_excerpt}</div>}
          <div className="text-slate-500 mt-1">Prior stored record: {JSON.stringify(res.prior_record)}</div>
        </div>
      )}
    </div>
  );
}
